import BusinessIcon from "@mui/icons-material/Business";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

type AccountPageHeaderProps = {
  logoUrl: string | null;
  clinicName?: string;
  clinicId?: string;
  userName?: string;
  userRole?: string;
  onGoToDashboard: () => void;
};

export default function AccountPageHeader({
  logoUrl,
  clinicName,
  clinicId,
  userName,
  userRole,
  onGoToDashboard,
}: AccountPageHeaderProps) {
  const displayName = clinicName?.trim() || "My Account";

  return (
    <header className="relative shrink-0 overflow-hidden border-b border-slate-200/70 bg-gradient-to-br from-white via-slate-50/90 to-primary/[0.04] dark:border-slate-700/60 dark:from-slate-900 dark:via-slate-800/95 dark:to-primary/10">
      <div
        className="pointer-events-none absolute -left-16 -top-16 h-40 w-40 rounded-full bg-primary/[0.06] blur-3xl dark:bg-primary/12"
        aria-hidden
      />

      <div className="relative flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4 sm:py-3.5 xl:px-5">
        <div className="flex min-w-0 items-center gap-3">
          <div className="relative shrink-0">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Clinic logo"
                className="h-11 w-11 rounded-xl border border-slate-200/80 bg-white object-contain p-1 shadow-sm ring-1 ring-slate-200/60 dark:border-slate-600/80 dark:bg-slate-800 dark:ring-slate-600/50 sm:h-12 sm:w-12"
              />
            ) : (
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primaryLight shadow-sm ring-1 ring-primary/20 sm:h-12 sm:w-12">
                <BusinessIcon sx={{ fontSize: 24 }} className="text-white" />
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="mb-0.5 flex flex-wrap items-center gap-1.5">
              <span className="inline-flex rounded-full bg-primary/10 px-2 py-px text-[9px] font-bold uppercase tracking-wider text-primary dark:bg-primary/20">
                Account
              </span>
              {clinicId ? (
                <span className="rounded bg-slate-100/90 px-1.5 py-px font-mono text-[9px] text-slate-500 dark:bg-slate-800/80 dark:text-slate-400">
                  {clinicId}
                </span>
              ) : null}
            </div>

            <h1 className="truncate text-lg font-bold tracking-tight text-slate-900 dark:text-white sm:text-xl">
              {displayName}
            </h1>

            {userName ? (
              <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                <span className="font-medium capitalize text-slate-700 dark:text-slate-300">
                  {userName}
                </span>
                {userRole ? (
                  <span className="text-slate-400 dark:text-slate-500"> · {userRole}</span>
                ) : null}
              </p>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          onClick={onGoToDashboard}
          className="group inline-flex shrink-0 items-center justify-center gap-1.5 self-start rounded-lg bg-primary px-3.5 py-2 text-xs font-semibold text-white shadow-sm ring-1 ring-primary/20 transition hover:bg-primary/95 sm:self-center sm:text-sm"
        >
          <DashboardOutlinedIcon sx={{ fontSize: 16 }} />
          Go to clinic
          <ArrowForwardIcon
            sx={{ fontSize: 14 }}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </button>
      </div>
    </header>
  );
}
